/**
 * The named entries a recursive REMOVE takes away (S8 Task 8.11): the root's own named entries as checkboxes, plus any
 * other object ID typed in (descendants may name principals the root does not). Base entries and masks are never
 * removable (`validateAclPatch`); Azure recalculates the mask of every path it removes from (row R5 vi).
 */
import { useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { aclEntryKey as keyOf } from '../../../../../core/engines/azureStorageAcl';
import type { StorageAclEntry, StorageAclScope } from '../../../../../types/azureStorage';
import type { RemoveEntry } from '../aclPropagate';

type Props = { rootEntries: StorageAclEntry[]; value: RemoveEntry[]; displayNames?: Record<string, string>; lockedReason?: string; onChange: (next: RemoveEntry[]) => void };

const label = (e: RemoveEntry, names: Record<string, string>): string => `${e.scope === 'default' ? 'Default ' : ''}${e.type} ${names[e.id] ? `${names[e.id]} (${e.id})` : e.id}`;

export function AclRemoveList({ rootEntries, value, displayNames = {}, lockedReason, onChange }: Props) {
  const [id, setId] = useState('');
  const [scope, setScope] = useState<StorageAclScope>('access');
  const offered = rootEntries.filter((e): e is StorageAclEntry & RemoveEntry => (e.type === 'user' || e.type === 'group') && !!e.id).map((e) => ({ scope: e.scope, type: e.type, id: e.id }));
  const extra = value.filter((e) => !offered.some((o) => keyOf(o) === keyOf(e)));
  const selected = new Set(value.map(keyOf));
  const toggle = (entry: RemoveEntry, on: boolean) => onChange(on ? [...value, entry] : value.filter((e) => keyOf(e) !== keyOf(entry)));
  const add = (type: 'user' | 'group') => {
    const trimmed = id.trim();
    if (!trimmed || selected.has(keyOf({ scope, type, id: trimmed }))) return;
    onChange([...value, { scope, type, id: trimmed }]);
    setId('');
  };
  return (
    <div className="grid gap-2 text-xs" data-testid="hns-propagate-remove-list">
      {[...offered, ...extra].map((entry) => (
        <label key={keyOf(entry)} className="flex items-center gap-2">
          <Checkbox checked={selected.has(keyOf(entry))} disabled={!!lockedReason} title={lockedReason} data-testid={`hns-propagate-remove-${entry.scope}-${entry.type}-${entry.id}`} onCheckedChange={(checked) => toggle(entry, checked === true)} />
          <span className="break-words [overflow-wrap:anywhere]">{label(entry, displayNames)}</span>
        </label>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="ghost" size="xs" disabled={!!lockedReason} title={lockedReason} aria-pressed={scope === 'default'} onClick={() => setScope(scope === 'access' ? 'default' : 'access')} data-testid="hns-propagate-remove-scope">{scope === 'access' ? 'Access entry' : 'Default entry'}</Button>
        <Input spellCheck={false} autoComplete="off" className="h-7 w-72 text-xs" placeholder="Object ID of a user or group" value={id} disabled={!!lockedReason} title={lockedReason} onChange={(event) => setId(event.target.value)} aria-label="Object ID to remove" data-testid="hns-propagate-remove-id" />
        <Button type="button" variant="outline" size="xs" disabled={!!lockedReason} title={lockedReason} onClick={() => add('user')} data-testid="hns-propagate-remove-add-user">Add user</Button>
        <Button type="button" variant="outline" size="xs" disabled={!!lockedReason} title={lockedReason} onClick={() => add('group')} data-testid="hns-propagate-remove-add-group">Add group</Button>
      </div>
    </div>
  );
}
